import { useEffect, useState, useCallback } from 'react'
import {
  Lightbulb,
  RefreshCw,
  Check,
  X,
  PiggyBank,
  ShieldCheck,
  Receipt,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  CalendarClock,
  Scale,
} from 'lucide-react'
import { getInsightsService } from '@/services/insights'
import { useAuth } from '@/contexts/AuthContext'
import {
  INSIGHT_TYPE_LABELS,
  type Insight,
  type InsightType,
} from '@/types/insight.types'

/** Lucide icon per insight type. */
const TYPE_ICON: Record<InsightType, typeof Lightbulb> = {
  tax_saving: PiggyBank,
  compliance: ShieldCheck,
  deduction: Receipt,
  investment: TrendingUp,
  risk_alert: AlertTriangle,
  optimization: Sparkles,
  deadline: CalendarClock,
  regulatory: Scale,
}

/** Categorical tint per insight type (documented status families only). */
const TYPE_TINT: Record<InsightType, string> = {
  tax_saving: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  compliance: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  deduction: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  investment: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  risk_alert: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  optimization: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  deadline: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',
  regulatory: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
}

const MAX_VISIBLE = 4

interface InsightsPanelProps {
  /** How many top insights to show. */
  limit?: number
}

export default function InsightsPanel({ limit = MAX_VISIBLE }: InsightsPanelProps) {
  const { hasPermission } = useAuth()
  const canGenerate = hasPermission('txh.insight.generate')
  const canDismiss = hasPermission('txh.insight.dismiss')
  const canApply = hasPermission('txh.insight.apply')
  const service = getInsightsService()

  const [insights, setInsights] = useState<Insight[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await service.list({ status: 'active' })
      setInsights(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load insights.')
    } finally {
      setLoading(false)
    }
  }, [service])

  useEffect(() => {
    load()
  }, [load])

  const handleRefresh = async () => {
    if (!canGenerate) return
    setRefreshing(true)
    setError(null)
    try {
      // Generate fresh insights against the customers already surfaced, then reload.
      const customerIds = Array.from(new Set(insights.map((i) => i.customerId))).slice(0, 1)
      if (customerIds.length > 0) {
        await service.generate({ customerId: customerIds[0], forceRefresh: true })
      }
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not refresh insights.')
    } finally {
      setRefreshing(false)
    }
  }

  const handleDismiss = async (id: string) => {
    if (!canDismiss) return
    setBusyId(id)
    try {
      await service.dismiss(id)
      setInsights((prev) => prev.filter((i) => i.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not dismiss insight.')
    } finally {
      setBusyId(null)
    }
  }

  const handleApply = async (id: string) => {
    if (!canApply) return
    setBusyId(id)
    try {
      await service.apply(id)
      setInsights((prev) => prev.filter((i) => i.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not apply insight.')
    } finally {
      setBusyId(null)
    }
  }

  const visible = insights.slice(0, limit)

  return (
    <div className="card card-elevated p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-primary-500" />
          Insights &amp; Recommendations
        </h3>
        {canGenerate && (
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="btn btn-outline btn-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-text-tertiary">
          <span className="loading loading-spinner loading-sm" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-10 space-y-3">
          <div className="w-12 h-12 bg-surface-overlay rounded-full flex items-center justify-center mx-auto">
            <Lightbulb className="w-6 h-6 text-text-tertiary" />
          </div>
          <p className="text-sm font-medium text-text-primary">No active insights</p>
          <p className="text-sm text-text-secondary">
            Refresh to generate new recommendations.
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {visible.map((insight) => {
            const Icon = TYPE_ICON[insight.insightType] ?? Lightbulb
            const tint = TYPE_TINT[insight.insightType] ?? TYPE_TINT.optimization
            const isBusy = busyId === insight.id
            return (
              <li
                key={insight.id}
                className="border border-surface-overlay rounded-lg p-4 space-y-2"
              >
                <div className="flex items-start gap-3">
                  <span className={`p-2 rounded-lg flex-shrink-0 ${tint}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-text-primary">
                        {insight.title}
                      </p>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${tint}`}
                      >
                        {INSIGHT_TYPE_LABELS[insight.insightType] ?? insight.insightType}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary mt-1 line-clamp-2">
                      {insight.description}
                    </p>
                    <p className="text-xs text-text-tertiary mt-1">
                      Confidence {Math.round(insight.confidenceScore * 100)}%
                    </p>
                  </div>
                </div>
                {(canDismiss || canApply) && (
                  <div className="flex items-center justify-end gap-2 pt-1">
                    {canDismiss && (
                      <button
                        onClick={() => handleDismiss(insight.id)}
                        disabled={isBusy}
                        className="btn btn-ghost btn-sm flex items-center gap-1"
                      >
                        <X className="w-4 h-4" />
                        Dismiss
                      </button>
                    )}
                    {canApply && (
                      <button
                        onClick={() => handleApply(insight.id)}
                        disabled={isBusy}
                        className="btn btn-outline btn-sm flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        Apply
                      </button>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
